import { useEffect, useState } from 'react';
import { Link, Navigate, NavLink, Route, Routes } from 'react-router-dom';
import { SecretReveal } from '../components/SecretReveal';
import { SettingsTable, type FieldDef } from '../components/SettingsTable';
import { TopBar } from '../components/TopBar';
import { AiModePanel } from '../components/settings/AiModePanel';
import { AssignmentPanel } from '../components/settings/AssignmentPanel';
import { AutomationBuilder } from '../components/settings/AutomationBuilder';
import { FlowsPanel } from '../components/settings/FlowsPanel';
import { KnownIssuesPanel } from '../components/settings/KnownIssuesPanel';
import { TeamsPanel } from '../components/settings/TeamsPanel';
import { AgentAccessPanel } from '../components/settings/AgentAccessPanel';
import { InfoPanel } from '../components/settings/InfoPanel';
import { SecurityPanel } from '../components/settings/SecurityPanel';
import { SlaInboxPanel } from '../components/settings/SlaInboxPanel';
import { WebhookDeliveriesPanel } from '../components/settings/WebhookDeliveriesPanel';
import { WorkingHoursPanel } from '../components/settings/WorkingHoursPanel';
import { useAuth } from '../hooks/useAuth';
import { useResource } from '../hooks/useResource';
import { api } from '../lib/api';
import { t, useLocale } from '../lib/i18n';
import type {
  ApiTokenRow,
  BotRow,
  CannedResponse,
  Inbox,
  Label,
  MacroRow,
  SlaRow,
  WebhookRow,
} from '../lib/types';

interface NavItem {
  to: string;
  labelKey: string;
  adminOnly?: boolean;
}

const NAV: NavItem[] = [
  { to: 'inboxes', labelKey: 'settings.nav.inboxes' },
  { to: 'ai-mode', labelKey: 'settings.nav.aiMode', adminOnly: true },
  { to: 'teams', labelKey: 'settings.nav.teams' },
  { to: 'agent-access', labelKey: 'settings.nav.agentAccess', adminOnly: true },
  { to: 'labels', labelKey: 'settings.nav.labels' },
  { to: 'canned', labelKey: 'settings.nav.canned' },
  { to: 'macros', labelKey: 'settings.nav.macros' },
  { to: 'business-hours', labelKey: 'settings.nav.workingHours', adminOnly: true },
  { to: 'sla', labelKey: 'settings.nav.sla', adminOnly: true },
  { to: 'assignment', labelKey: 'settings.nav.assignment', adminOnly: true },
  { to: 'automations', labelKey: 'settings.nav.automations', adminOnly: true },
  { to: 'flows', labelKey: 'settings.nav.flows', adminOnly: true },
  { to: 'known-issues', labelKey: 'settings.nav.knownIssues', adminOnly: true },
  { to: 'bots', labelKey: 'settings.nav.bots', adminOnly: true },
  { to: 'webhooks', labelKey: 'settings.nav.webhooks', adminOnly: true },
  { to: 'webhook-deliveries', labelKey: 'settings.nav.webhookDeliveries', adminOnly: true },
  { to: 'api-tokens', labelKey: 'settings.nav.apiTokens', adminOnly: true },
  { to: 'audit', labelKey: 'settings.nav.audit', adminOnly: true },
  { to: 'security', labelKey: 'settings.nav.security', adminOnly: true },
  { to: 'info', labelKey: 'settings.nav.info' },
];

export function SettingsPage() {
  const { user, claims, signOut } = useAuth();
  const isAdmin = claims?.role === 'tenant_admin' || claims?.role === 'super_admin';
  // Subscribe to locale changes so the nav re-renders on language switch.
  useLocale();

  // Settings used to swap the global nav out for a single back-link, so an
  // agent in here could not reach Contacts / Tickets / Guides / Reports, nor
  // sign out or switch tenant, without first going back to the inbox. TopBar
  // stays; the sidebar below is second-level nav within Settings.
  // TopBar is `fixed` and 40px (h-10) tall, hence the offset on the grid.
  return (
    <div className="min-h-screen bg-gray-50">
      <TopBar
        tenantCode={claims?.tenantCode ?? ''}
        userEmail={user?.email ?? undefined}
        onSignOut={signOut}
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '220px 1fr',
          height: 'calc(100vh - 40px)',
          marginTop: 40,
        }}
      >
        <aside style={{ borderRight: '1px solid #e5e7eb', padding: 16, overflowY: 'auto', background: 'white' }}>
          <Link to="/inbox" style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none' }}>
            ← {t('settings.backToInbox')}
          </Link>
          <h2 style={{ fontSize: 14, marginTop: 16, marginBottom: 8, color: '#6b7280' }}>
            {t('settings.title').toUpperCase()}
          </h2>
          {NAV.filter((n) => !n.adminOnly || isAdmin).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              style={({ isActive }) => ({
                display: 'block',
                padding: '8px 12px',
                borderRadius: 6,
                fontSize: 14,
                textDecoration: 'none',
                color: isActive ? '#2563eb' : '#374151',
                background: isActive ? '#eff6ff' : 'transparent',
                fontWeight: isActive ? 600 : 400,
              })}
            >
              {t(n.labelKey)}
            </NavLink>
          ))}
        </aside>
        <main style={{ padding: 24, overflowY: 'auto' }}>
          <Routes>
            <Route index element={<Navigate to="inboxes" replace />} />
            <Route path="inboxes" element={<InboxesPanel canEdit={isAdmin} />} />
            <Route path="ai-mode" element={<AiModePanel canEdit={isAdmin} />} />
            <Route path="teams" element={<TeamsPanel canEdit={isAdmin} />} />
            <Route path="labels" element={<LabelsPanel canEdit={isAdmin} />} />
            <Route path="canned" element={<CannedPanel canEdit={isAdmin} />} />
            <Route path="macros" element={<MacrosPanel canEdit={isAdmin} />} />
            <Route path="business-hours" element={<WorkingHoursPanel canEdit={isAdmin} />} />
            <Route path="sla" element={<SlaCombinedPanel canEdit={isAdmin} />} />
            <Route path="assignment" element={<AssignmentPanel canEdit={isAdmin} />} />
            <Route path="automations" element={<AutomationBuilder canEdit={isAdmin} />} />
            <Route path="flows" element={<FlowsPanel canEdit={isAdmin} />} />
            <Route path="known-issues" element={<KnownIssuesPanel canEdit={isAdmin} />} />
            <Route path="agent-access" element={<AgentAccessPanel canEdit={isAdmin} />} />
            <Route path="bots" element={<BotsPanel canEdit={isAdmin} />} />
            <Route path="webhooks" element={<WebhooksPanel canEdit={isAdmin} />} />
            <Route path="webhook-deliveries" element={<WebhookDeliveriesPanel />} />
            <Route path="api-tokens" element={<ApiTokensPanel canEdit={isAdmin} />} />
            <Route path="audit" element={<AuditPanel />} />
            <Route path="security" element={<SecurityPanel canEdit={isAdmin} />} />
            <Route path="info" element={<InfoPanel />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

// /settings/sla now combines the per-inbox SLA form (new) with the existing
// CRUD list of SLA policies. The per-inbox form sits above the table.
function SlaCombinedPanel({ canEdit }: { canEdit: boolean }) {
  return (
    <div>
      <SlaInboxPanel canEdit={canEdit} />
      <div style={{ marginTop: 24 }}>
        <SlaPanel canEdit={canEdit} />
      </div>
    </div>
  );
}

interface InboxWithBranding extends Inbox {
  logoUrl?: string;
  primaryColor?: string;
  brandName?: string;
}

function InboxesPanel({ canEdit }: { canEdit: boolean }) {
  const { items, loading } = useResource<InboxWithBranding>('inboxes');
  const fields: FieldDef<InboxWithBranding>[] = [
    { key: 'name', label: t('common.name') },
    { key: 'channel', label: 'Channel', type: 'select', options: ['web', 'email', 'whatsapp', 'sms', 'api'], width: 120 },
    { key: 'greeting', label: 'Greeting' },
    { key: 'brandName', label: t('settings.branding.brandName'), width: 140 },
    { key: 'logoUrl', label: t('settings.branding.logo') },
    { key: 'primaryColor', label: t('settings.branding.primaryColor'), type: 'color', width: 100 },
    {
      key: 'agentUids',
      label: 'Agents',
      width: 80,
      render: (v) => (Array.isArray(v) ? `${v.length}` : '0'),
    },
  ];
  return (
    <SettingsTable<InboxWithBranding>
      resource="inboxes"
      title={t('settings.nav.inboxes')}
      items={items}
      loading={loading}
      fields={fields}
      defaults={
        {
          name: 'New inbox',
          channel: 'web',
          agentUids: [],
          primaryColor: '#2563eb',
        } as Partial<InboxWithBranding>
      }
      canEdit={canEdit}
    />
  );
}

function LabelsPanel({ canEdit }: { canEdit: boolean }) {
  const { items, loading } = useResource<Label>('labels');
  const fields: FieldDef<Label>[] = [
    { key: 'name', label: 'Name' },
    { key: 'color', label: 'Color', type: 'color', width: 80 },
    { key: 'description', label: 'Description' },
  ];
  return (
    <SettingsTable<Label>
      resource="labels"
      title="Labels"
      items={items}
      loading={loading}
      fields={fields}
      defaults={{ name: 'new-label', color: '#3B82F6' } as Partial<Label>}
      canEdit={canEdit}
    />
  );
}

function CannedPanel({ canEdit }: { canEdit: boolean }) {
  const { items, loading } = useResource<CannedResponse>('canned_responses');
  // Folder stays free text rather than type:'select' — agents make a folder by
  // naming it, and the select renderer falls back to options[0] for a blank
  // value, which would silently file every unfiled response.
  const fields: FieldDef<CannedResponse>[] = [
    { key: 'shortcut', label: 'Shortcut', width: 160 },
    { key: 'title', label: 'Title', width: 180 },
    { key: 'folder', label: 'Folder', width: 140 },
    { key: 'content', label: 'Content' },
  ];
  return (
    <SettingsTable<CannedResponse>
      resource="canned"
      title="Canned responses"
      items={items}
      loading={loading}
      fields={fields}
      defaults={{ shortcut: '/new', content: 'Hello, ' } as Partial<CannedResponse>}
      canEdit={canEdit}
    />
  );
}

function MacrosPanel({ canEdit }: { canEdit: boolean }) {
  const { items, loading } = useResource<MacroRow>('macros');
  const fields: FieldDef<MacroRow>[] = [
    { key: 'name', label: 'Name' },
    { key: 'enabled', label: 'Enabled', width: 80, render: (v) => (v ? 'yes' : 'no') },
    { key: 'actions', label: 'Actions', render: (v) => (Array.isArray(v) ? `${v.length}` : '0') },
  ];
  return (
    <SettingsTable<MacroRow>
      resource="macros"
      title="Macros"
      items={items}
      loading={loading}
      fields={fields}
      defaults={{ name: 'New macro', actions: [], enabled: true } as Partial<MacroRow>}
      canEdit={canEdit}
    />
  );
}

function SlaPanel({ canEdit }: { canEdit: boolean }) {
  const { items, loading } = useResource<SlaRow>('sla_policies');
  const fields: FieldDef<SlaRow>[] = [
    { key: 'name', label: 'Name' },
    { key: 'firstResponseMin', label: 'First (min)', width: 90 },
    { key: 'nextResponseMin', label: 'Next (min)', width: 90 },
    { key: 'resolutionMin', label: 'Resolve (min)', width: 100 },
    { key: 'breachLabel', label: 'Breach label' },
  ];
  return (
    <SettingsTable<SlaRow>
      resource="sla-policies"
      title="SLA policies"
      items={items}
      loading={loading}
      fields={fields}
      defaults={{ name: 'New SLA', breachLabel: 'sla-breach' } as Partial<SlaRow>}
      canEdit={canEdit}
    />
  );
}

function BotsPanel({ canEdit }: { canEdit: boolean }) {
  const { items, loading } = useResource<BotRow>('bots');
  const [reveal, setReveal] = useState<{ title: string; values: Record<string, string> } | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: '', webhookUrl: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function submitCreate() {
    if (!draft.name.trim()) { setErr('Name required'); return; }
    setBusy(true);
    setErr(null);
    try {
      const body: Record<string, unknown> = { name: draft.name.trim(), inboxIds: [] };
      if (draft.webhookUrl.trim()) body.webhookUrl = draft.webhookUrl.trim();
      const result = await api.create<{ bot: BotRow; token: string; hmacSecret: string }>('bots', body);
      setReveal({ title: 'Bot created — copy creds NOW', values: { Token: result.token, 'HMAC Secret': result.hmacSecret } });
      setCreating(false);
      setDraft({ name: '', webhookUrl: '' });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  async function rotate(id: string) {
    if (!confirm('Rotate token? Old token will stop working immediately.')) return;
    const r = await api.rotateBot(id);
    setReveal({ title: 'New token — copy NOW', values: { Token: r.token } });
  }
  return (
    <section>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>Bots</h2>
        {canEdit && (
          <button onClick={() => { setCreating(true); setErr(null); }} style={btnPrimary}>+ New bot</button>
        )}
      </div>
      {loading && <div>Loading…</div>}
      {creating && (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 6, padding: 12, marginBottom: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>New bot</div>
          <div style={{ display: 'grid', gap: 8, gridTemplateColumns: '1fr 1fr' }}>
            <label style={{ fontSize: 12, color: '#6b7280' }}>
              Name<br />
              <input
                autoFocus
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="e.g. order-status-bot"
                style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}
              />
            </label>
            <label style={{ fontSize: 12, color: '#6b7280' }}>
              Webhook URL (optional)<br />
              <input
                value={draft.webhookUrl}
                onChange={(e) => setDraft({ ...draft, webhookUrl: e.target.value })}
                placeholder="https://your-bot.example.com/webhook"
                style={{ width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}
              />
            </label>
          </div>
          {err && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 8 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <button onClick={() => { setCreating(false); setErr(null); }} style={btnSecondary}>Cancel</button>
            <button onClick={submitCreate} disabled={busy} style={btnPrimary}>{busy ? 'Creating…' : 'Create bot'}</button>
          </div>
        </div>
      )}
      <table style={tableStyle}>
        <thead>
          <tr style={trHead}>
            <th style={th}>Name</th>
            <th style={th}>Inboxes</th>
            <th style={th}>Enabled</th>
            <th style={th}>Created</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((b) => (
            <tr key={b.id} style={trRow}>
              <td style={td}>{b.name}</td>
              <td style={td}>{b.inboxIds?.length ?? 0}</td>
              <td style={td}>{b.enabled ? 'yes' : 'no'}</td>
              <td style={td}>{new Date(b.createdAt).toLocaleString()}</td>
              <td style={td}>
                {canEdit && <button onClick={() => rotate(b.id)} style={btnSecondary}>Rotate</button>}
                {canEdit && (
                  <button onClick={() => api.delete('bots', b.id).then(() => location.reload())} style={btnDanger}>
                    Delete
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {reveal && <SecretReveal {...reveal} onClose={() => setReveal(null)} />}
    </section>
  );
}

function WebhooksPanel({ canEdit }: { canEdit: boolean }) {
  const { items, loading } = useResource<WebhookRow>('webhooks');
  const [reveal, setReveal] = useState<{ title: string; values: Record<string, string> } | null>(null);
  async function createWebhook() {
    const url = prompt('Webhook URL?');
    if (!url) return;
    const eventsRaw = prompt(
      'Events (comma-separated)',
      'conversation_created,message_created,conversation_status_changed'
    );
    if (!eventsRaw) return;
    const events = eventsRaw.split(',').map((s) => s.trim()).filter(Boolean);
    const result = await api.create<{ id: string; hmacSecret: string }>('webhooks', { url, events });
    setReveal({ title: 'Webhook created — copy HMAC secret NOW', values: { 'HMAC Secret': result.hmacSecret } });
  }
  return (
    <section>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>Outgoing webhooks</h2>
        {canEdit && <button onClick={createWebhook} style={btnPrimary}>+ New webhook</button>}
      </div>
      {loading && <div>Loading…</div>}
      <table style={tableStyle}>
        <thead>
          <tr style={trHead}>
            <th style={th}>URL</th>
            <th style={th}>Events</th>
            <th style={th}>Enabled</th>
            <th style={th}>Failures</th>
            <th style={th}>Last success</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((w) => (
            <tr key={w.id} style={trRow}>
              <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>{w.url}</td>
              <td style={td}>{w.events.join(', ')}</td>
              <td style={td}>{w.enabled ? 'yes' : 'no'}</td>
              <td style={td}>{w.consecutiveFailures}</td>
              <td style={td}>{w.lastSuccessAt ? new Date(w.lastSuccessAt).toLocaleString() : '—'}</td>
              <td style={td}>
                {canEdit && (
                  <button onClick={() => api.delete('webhooks', w.id).then(() => location.reload())} style={btnDanger}>
                    Delete
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {reveal && <SecretReveal {...reveal} onClose={() => setReveal(null)} />}
    </section>
  );
}

function ApiTokensPanel({ canEdit }: { canEdit: boolean }) {
  const { items, loading } = useResource<ApiTokenRow>('api_tokens');
  const [reveal, setReveal] = useState<{ title: string; values: Record<string, string> } | null>(null);
  async function createToken() {
    const name = prompt('Token name?');
    if (!name) return;
    const scopesRaw = prompt(
      'Scopes (comma-separated)',
      'conversations:read,messages:read,contacts:read'
    );
    if (!scopesRaw) return;
    const scopes = scopesRaw.split(',').map((s) => s.trim()).filter(Boolean);
    const result = await api.create<{ id: string; token: string; scopes: string[] }>('api-tokens', { name, scopes });
    setReveal({ title: 'API token created — copy NOW', values: { Token: result.token } });
  }
  return (
    <section>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>API tokens</h2>
        {canEdit && <button onClick={createToken} style={btnPrimary}>+ New token</button>}
      </div>
      {loading && <div>Loading…</div>}
      <table style={tableStyle}>
        <thead>
          <tr style={trHead}>
            <th style={th}>Name</th>
            <th style={th}>Scopes</th>
            <th style={th}>Enabled</th>
            <th style={th}>Created</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((t) => (
            <tr key={t.id} style={trRow}>
              <td style={td}>{t.name}</td>
              <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>{t.scopes.join(', ')}</td>
              <td style={td}>{t.enabled ? 'yes' : 'no'}</td>
              <td style={td}>{new Date(t.createdAt).toLocaleString()}</td>
              <td style={td}>
                {canEdit && (
                  <button onClick={() => api.delete('api-tokens', t.id).then(() => location.reload())} style={btnDanger}>
                    Revoke
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {reveal && <SecretReveal {...reveal} onClose={() => setReveal(null)} />}
    </section>
  );
}

function AuditPanel() {
  const [items, setItems] = useState<Array<Record<string, unknown>>>([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState<number | null>(null);

  useEffect(() => {
    api
      .listAuditLogs(100)
      .then((r) => {
        setItems(r.items);
        setCursor(r.nextCursor);
      })
      .finally(() => setLoading(false));
  }, []);

  async function loadMore() {
    if (!cursor) return;
    const r = await api.listAuditLogs(100, cursor);
    setItems((cur) => [...cur, ...r.items]);
    setCursor(r.nextCursor);
  }

  return (
    <section>
      <h2 style={{ margin: 0, fontSize: 18, marginBottom: 12 }}>Audit log</h2>
      {loading && <div>Loading…</div>}
      <table style={tableStyle}>
        <thead>
          <tr style={trHead}>
            <th style={th}>Timestamp</th>
            <th style={th}>Actor</th>
            <th style={th}>Action</th>
            <th style={th}>Target</th>
            <th style={th}>Detail</th>
          </tr>
        </thead>
        <tbody>
          {items.map((row) => {
            const tsRaw =
              (row.timestamp as number | string | undefined) ??
              (row.at as number | string | undefined) ??
              (row.createdAt as number | string | undefined);
            let ts: Date | null = null;
            if (typeof tsRaw === 'number') ts = new Date(tsRaw);
            else if (typeof tsRaw === 'string') {
              // Try ISO string first; fall back to number-encoded ms
              const isoTry = new Date(tsRaw);
              ts = !Number.isNaN(isoTry.getTime()) ? isoTry : new Date(Number(tsRaw));
            }
            const actor =
              (row.actorEmail as string | undefined) ??
              (row.actorName as string | undefined) ??
              (row.actor as string | undefined) ??
              (row.actorId as string | undefined) ??
              (row.actorUid as string | undefined) ??
              (row.userId as string | undefined) ??
              (row.user as string | undefined) ??
              '—';
            const target =
              (row.target as string | undefined) ??
              (row.targetId as string | undefined) ??
              (row.resourceId as string | undefined);
            const detail = row.new ?? row.details ?? row.metadata ?? row.payload;
            return (
              <tr key={String(row.id)} style={trRow}>
                <td style={td}>{ts && !Number.isNaN(ts.getTime()) ? ts.toLocaleString() : '—'}</td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>{String(actor)}</td>
                <td style={td}>{String(row.action ?? '—')}</td>
                <td style={td}>{target ? String(target).slice(0, 24) : '—'}</td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: 11 }}>
                  {detail ? JSON.stringify(detail).slice(0, 80) : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {cursor && (
        <button onClick={loadMore} style={{ ...btnSecondary, marginTop: 12 }}>
          Load more
        </button>
      )}
    </section>
  );
}

const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const trHead: React.CSSProperties = { borderBottom: '1px solid #e5e7eb', textAlign: 'left', color: '#6b7280' };
const trRow: React.CSSProperties = { borderBottom: '1px solid #f3f4f6' };
const th: React.CSSProperties = { padding: '8px 4px' };
const td: React.CSSProperties = { padding: '6px 4px' };
const btnPrimary: React.CSSProperties = {
  padding: '6px 12px',
  background: '#2563eb',
  color: 'white',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 13,
};
const btnSecondary: React.CSSProperties = {
  padding: '4px 10px',
  background: 'white',
  color: '#374151',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 12,
  marginRight: 4,
};
const btnDanger: React.CSSProperties = {
  padding: '4px 10px',
  background: 'white',
  color: '#dc2626',
  border: '1px solid #fca5a5',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 12,
};
